import type { Dispatch, SetStateAction } from 'react';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { logError } from '~/common/utils/log';
import { useGetBranding } from '~/domains/auth/hooks/useGetBranding';
import { getAccessTokenUserId } from '~/domains/auth/utils/getAccessTokenData';
import { removeTokens } from '~/domains/auth/utils/removeTokens';
import {
  getStoredUser,
  reportEvent,
  setAnalyticsUser,
  updateAnalyticsUser,
} from '~/domains/analytics';
import { getCurrentUserW9Status } from '~/domains/payments/withdrawal/services/w9.service.api';
import type { APIW9Record } from '~/domains/payments/withdrawal/services/w9.types';
import { Routes } from '~/domains/common/constants/routes';
import { fetchUserWithRefreshToken } from '~/services/identity/authenticated/identity.service.api';
import { getUserSsnStatus, meDetailed } from '~/services/users/users.service.api';
import type { APIAuthUserData, APIUserResponse, UserSsnStatus } from '~/services/users/types';
import { getUserCohorts } from '~/services/marketing/marketing.service.api';
import {
  COHORT_KEYS,
  COHORT_REFETCH_INTERVAL,
} from '~/services/marketing/marketing-cohorts.interface';

import { UserProviderQueryKeys } from './query';
import { isLoadingFixedForDisabledQuery } from '~/domains/common/utils/isLoadingFixedForEnabled';

type UserContextType = {
  addUserCohort: (cohortKey: string) => void;
  removeUserCohort: (cohortKey: string) => void;
  clearRedirect: () => void;
  getRedirect: () => string | null;
  initializing: any;
  isBetterpoolEmail: boolean;
  isSignUpInitialised: boolean;
  isUserDetailsFetching: boolean;
  isUserSignedIn?: boolean;
  refreshUser: VoidFunction;
  refreshUserDetails: VoidFunction;
  refreshUserSsnStatus: VoidFunction;
  setIsSignUpInitialised: Dispatch<SetStateAction<boolean>>;
  setRedirect: (redirect: string) => void;
  setUser: Dispatch<SetStateAction<APIAuthUserData>>;
  user: APIAuthUserData;
  userCohorts: string[];
  userId: string | null;
  userDetails: APIUserResponse['data'];
  userSsnStatus: UserSsnStatus;
  isW9StatusLoading: boolean;
  ssnExists: boolean;
  latestW9: APIW9Record | null;
  w9Blocked: boolean;
  w9Country?: string;
};

const UserContext = createContext<UserContextType>({
  addUserCohort: () => {},
  removeUserCohort: () => {},
  user: null,
  userCohorts: [],
  userId: null,
  userDetails: null,
  userSsnStatus: null,
  isW9StatusLoading: false,
  ssnExists: false,
  latestW9: null,
  w9Blocked: false,
  w9Country: undefined,
  refreshUser: () => {},
  refreshUserDetails: () => {},
  refreshUserSsnStatus: async () => {},
  setUser: null,
  initializing: null,
  setRedirect: () => {},
  getRedirect: () => null,
  clearRedirect: () => {},
  isBetterpoolEmail: false,
  isUserDetailsFetching: false,
  isSignUpInitialised: false,
  setIsSignUpInitialised: () => {},
});

interface UserContextProviderProps {
  children: React.ReactNode;
}

function UserContextProvider({ children }: UserContextProviderProps) {
  const [user, setUser] = useState<APIAuthUserData>(null);
  const [userCohorts, setUserCohorts] = useState<string[]>([]);
  const [initializing, setInitializing] = useState(true);
  const [userResolved, setUserResolved] = useState(false);
  const [isSignUpInitialised, setIsSignUpInitialised] = useState(false);

  const accessToken = Cookies.get('accessToken');
  const refreshToken = Cookies.get('refreshToken');
  const storedUser = getStoredUser();
  const accessTokenUserId = getAccessTokenUserId(accessToken);
  const isStoredUserFromAnotherAccount =
    !!storedUser && !!accessTokenUserId && storedUser.id !== accessTokenUserId;
  const router = useRouter();
  const { isEOBrand } = useGetBranding();

  // --------------- REDIRECTS ---------------
  const redirectKey = 'sign_in_redirect';
  const getRedirect = useCallback((): string | null => {
    try {
      return window.sessionStorage.getItem(redirectKey);
    } catch {
      return null;
    }
  }, [redirectKey]);

  const setRedirect = useCallback(
    (redirect: string) => {
      try {
        window.sessionStorage.setItem(redirectKey, redirect);
      } catch {} // eslint-disable-line no-empty -- Redirect persistence is optional when storage is blocked.
    },
    [redirectKey]
  );

  const clearRedirect = useCallback(() => {
    try {
      window.sessionStorage.removeItem(redirectKey);
    } catch {} // eslint-disable-line no-empty -- Redirect cleanup must not prevent completing sign-in.
  }, [redirectKey]);

  // --------------- USER FUNCTIONS ---------------
  const {
    data: userDetails,
    refetch: refreshUserDetails,
    isFetching: isUserDetailsFetching,
    // eslint-disable-next-line local-rules/require-query-stale-time
  } = useQuery({
    queryKey: UserProviderQueryKeys.USER_DETAILS(user?.id),
    enabled: user !== null,
    queryFn: meDetailed,
    onError: (error: AxiosError) => {
      if ([404, 401].includes(error?.response?.status)) {
        return;
      }

      logError(error);
    },
  });

  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: userSsnStatus, refetch: refreshSsnStatus } = useQuery({
    queryKey: UserProviderQueryKeys.USER_SSN_STATUS(user?.id),
    enabled: user !== null,
    queryFn: getUserSsnStatus,
    onError: (error: AxiosError) => {
      if ([404, 401].includes(error?.response?.status)) {
        return;
      }

      logError(error);
    },
  });

  const {
    data: w9Status,
    refetch: refreshW9Status,
    isLoading: isW9StatusLoading,
    fetchStatus: w9StatusFetchStatus,
    // eslint-disable-next-line local-rules/require-query-stale-time
  } = useQuery({
    queryKey: UserProviderQueryKeys.USER_W9_STATUS(user?.id),
    enabled: user !== null,
    queryFn: getCurrentUserW9Status,
    onError: (error: AxiosError) => {
      if ([404, 401].includes(error?.response?.status)) {
        return;
      }

      logError(error);
    },
  });

  const { mutate: refreshUser } = useMutation({
    mutationKey: UserProviderQueryKeys.USER_AUTH,
    mutationFn: fetchUserWithRefreshToken,
    onSuccess: (data) => {
      setUser(data.data);
    },
    onError: (error: AxiosError) => {
      logError('failed to refresh', error);
    },
    onSettled: () => {
      setUserResolved(true);
      setInitializing(false);
    },
  });

  useEffect(() => {
    // Do not automatically refresh the user for the EO brand authentication
    // Note: useGetBranding() hook already limits the check to the AUTH pages only
    if (isEOBrand) {
      setUserResolved(true);
      return;
    }

    // During sign-up, completeAuthentication handles its own user fetching.
    // Calling refreshUser() here would race with that flow (fetchRefreshToken
    // rotates the access token, potentially invalidating the in-flight me() call).
    if (isSignUpInitialised) {
      return;
    }

    const bootStoredUser = !user && isStoredUserFromAnotherAccount ? null : storedUser;

    if (!bootStoredUser && !refreshToken) {
      if (accessToken) {
        removeTokens();
      }
      setUserResolved(true);
      setInitializing(false);
      return;
    }

    if (!bootStoredUser && refreshToken) {
      refreshUser();
      return;
    }

    if (bootStoredUser && !user) {
      setUser(bootStoredUser);
      setUserResolved(true);
      setInitializing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refreshUser,
    JSON.stringify(storedUser),
    refreshToken,
    user,
    isSignUpInitialised,
    isStoredUserFromAnotherAccount,
  ]);

  const timerIdRef = useRef(null);
  useEffect(() => {
    const stopPolling = () => clearInterval(timerIdRef.current);
    const pollingCallback = () => {
      const hasRefreshToken = Cookies.get('refreshToken');

      if (!hasRefreshToken) {
        stopPolling();
        router.replace(Routes.logout());
        return;
      }

      const sessionUserId = getAccessTokenUserId();

      if (sessionUserId && sessionUserId !== user.id) {
        stopPolling();
        window.location.reload();
      }
    };
    const startPolling = () => {
      timerIdRef.current = setInterval(pollingCallback, 10000);
    };

    if (user) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [user]);

  // --------------- ANALYTICS ---------------
  // Set new user
  useEffect(() => {
    if (user) {
      setAnalyticsUser(user);
    }
  }, [user, user?.id]);

  // Update user details
  useEffect(() => {
    if (userDetails) {
      updateAnalyticsUser(userDetails.data);
    }
  }, [userDetails]);

  // --------------- USER COHORTS ---------------
  // Get user's cohorts
  useQuery({
    queryKey: UserProviderQueryKeys.USER_COHORTS(user?.id),
    queryFn: () => getUserCohorts(),
    enabled: !!user?.id,
    staleTime: COHORT_REFETCH_INTERVAL,
    onSuccess: (data) => {
      setUserCohorts(data);
    },
    onError: (error: AxiosError) => {
      logError('failed to get user cohorts', error);
    },
  });

  // Potential whaler event
  useEffect(() => {
    if (userCohorts.includes(COHORT_KEYS.POTENTIAL_WHALER)) {
      reportEvent('QuickPicks > Potential Whaler');
    }
  }, [userCohorts]);

  const removeUserCohort = useCallback((cohortKey: string) => {
    setUserCohorts((prev) => prev.filter((cohort) => cohort !== cohortKey));
  }, []);

  const addUserCohort = useCallback((cohortKey: string) => {
    setUserCohorts((prev) => [...prev, cohortKey]);
  }, []);

  const refreshUserSsnStatus = useCallback(() => {
    refreshSsnStatus();
    refreshW9Status();
  }, [refreshSsnStatus, refreshW9Status]);

  const value = useMemo(
    () => ({
      addUserCohort,
      removeUserCohort,
      isUserSignedIn: !!refreshToken,
      user,
      userId: user?.id,
      userDetails: userDetails?.data,
      userCohorts,
      userSsnStatus: userSsnStatus?.data?.userSsnStatus,
      isW9StatusLoading: isLoadingFixedForDisabledQuery(isW9StatusLoading, w9StatusFetchStatus),
      ssnExists: w9Status?.data?.ssnExists,
      latestW9: w9Status?.data?.latestW9,
      w9Blocked: w9Status?.data?.w9Blocked,
      w9Country: w9Status?.data?.country,
      refreshUser,
      refreshUserDetails,
      refreshUserSsnStatus,
      initializing,
      isUserDetailsFetching,
      setUser,
      setRedirect,
      getRedirect,
      clearRedirect,
      isBetterpoolEmail: user?.email?.endsWith('@betterpool.com'),
      isSignUpInitialised,
      setIsSignUpInitialised,
    }),
    [
      addUserCohort,
      removeUserCohort,
      clearRedirect,
      getRedirect,
      initializing,
      isSignUpInitialised,
      refreshToken,
      refreshUser,
      refreshUserDetails,
      refreshUserSsnStatus,
      isUserDetailsFetching,
      setRedirect,
      setIsSignUpInitialised,
      user,
      userCohorts,
      userDetails?.data,
      userSsnStatus?.data?.userSsnStatus,
      isW9StatusLoading,
      w9Status?.data?.ssnExists,
      w9Status?.data?.latestW9,
      w9Status?.data?.w9Blocked,
      w9Status?.data?.country,
    ]
  );

  if (!userResolved) {
    return null;
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export default UserContextProvider;

export { UserContext, type UserContextType };
